import React, { useEffect, useState } from "react";

import { instructorSchedulesService } from "api/services/instructor-schedules.service";
import ContentHeader from "components/Layout/ContentHeader";
import ReservationListModal, { type ReservationCountChange } from "components/schedule-group/ReservationListModal";
import ScheduleDetailModal from "components/schedule-group/ScheduleDetailModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Table, { Column } from "components/UI/Table";
import Tabs from "components/UI/Tabs";
import { extractList, getApiErrorMessage } from "utils/api-error";
import { SCHEDULE_STATUS_TABS, SCHEDULE_SORT_OPTIONS } from "utils/constant/schedule";
import { sortSchedulesByDate } from "utils/schedule";

import type { InstructorScheduleListItem, ScheduleStatus } from "api/types/instructor-schedules.types";
import type { ScheduleSortOrder } from "utils/type/schedule";
import "pages/AdminPage.scss";

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const InstructorScheduleList: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [items, setItems] = useState<InstructorScheduleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(getCurrentMonth());
  const [sortOrder, setSortOrder] = useState<ScheduleSortOrder>("latest");
  const [error, setError] = useState("");

  // 예약자 목록 모달
  const [reservationModal, setReservationModal] = useState<{
    open: boolean;
    scheduleId: number | null;
    title: string;
  }>({ open: false, scheduleId: null, title: "" });

  // 강습 상세 모달
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    scheduleId: number | null;
  }>({ open: false, scheduleId: null });

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};

      const pad = (n: number) => String(n).padStart(2, "0");
      const [year, month] = dateFilter.split("-").map(Number);
      const lastDate = new Date(year, month, 0).getDate();
      params.startDate = `${year}-${pad(month)}-01`;
      params.endDate = `${year}-${pad(month)}-${pad(lastDate)}`;

      const res = await instructorSchedulesService.list(params);
      setItems(extractList<InstructorScheduleListItem>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const filteredItems = items.filter((item) => {
    if (activeTab !== "all" && item.status !== activeTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return item.instructor_name.toLowerCase().includes(q) || item.lesson_product_name.toLowerCase().includes(q) || item.location_name.toLowerCase().includes(q);
  });

  const sortedItems = sortSchedulesByDate(filteredItems, sortOrder);

  const openReservations = (schedule: InstructorScheduleListItem) => {
    setReservationModal({
      open: true,
      scheduleId: schedule.id,
      title: `${schedule.lesson_product_display_name} - ${schedule.date}`,
    });
  };

  const closeReservations = () => {
    setReservationModal({ open: false, scheduleId: null, title: "" });
  };

  // 강제 예약 / 취소 시 리스트의 예약/대기 카운트를 즉시 반영 (낙관적 업데이트)
  const handleReservationChanged = (change: ReservationCountChange) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === change.scheduleId
          ? {
              ...it,
              current_bookings: Math.max(0, it.current_bookings + change.confirmedDelta),
              available_spots: Math.max(0, it.available_spots - change.confirmedDelta),
              waiting_count: Math.max(0, it.waiting_count + change.waitingDelta),
            }
          : it,
      ),
    );
  };

  const openDetail = (schedule: InstructorScheduleListItem) => {
    setDetailModal({ open: true, scheduleId: schedule.id });
  };

  const closeDetail = () => {
    setDetailModal({ open: false, scheduleId: null });
  };

  const columns: Column<InstructorScheduleListItem>[] = [
    {
      key: "lesson_product_display_name",
      title: "강습명",
    },
    { key: "instructor_name", title: "강사" },
    { key: "date", title: "날짜" },
    {
      key: "start_time",
      title: "시간",
      render: (v, row) => `${String(v).slice(0, 5)} ~ ${String(row.end_time).slice(0, 5)}`,
    },
    { key: "location_name", title: "장소" },
    {
      key: "status",
      title: "상태",
      render: (v, _row) => <Badge variant={v === "OPEN" ? "draft" : "rejected"}>{v}</Badge>,
    },
    {
      key: "current_bookings",
      title: "예약/정원",
      render: (v, row) => `${v}/${row.capacity}`,
    },
    {
      key: "actions",
      title: "예약자",
      render: (_, row) => (
        <div className="schedule-actions">
          {
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                openReservations(row);
              }}
            >
              보기
            </Button>
          }
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="강습 일정" description="강사가 등록한 강습 스케줄을 조회할 수 있습니다." />

      <div className="admin-page__card">
        <Tabs items={SCHEDULE_STATUS_TABS} activeKey={activeTab} onChange={(k) => setActiveTab(k as ScheduleStatus | "all")} />

        <div className="admin-page__filter">
          <Input placeholder="강사명, 강습명, 장소 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
          <input type="month" value={dateFilter} onChange={(e) => setDateFilter(e.target.value || getCurrentMonth())} className="schedule-date-input" />
          <Select aria-label="정렬" options={SCHEDULE_SORT_OPTIONS} value={sortOrder} onChange={(e) => setSortOrder(e.target.value as ScheduleSortOrder)} />
          {dateFilter !== getCurrentMonth() && (
            <Button size="sm" variant="ghost" onClick={() => setDateFilter(getCurrentMonth())}>
              이번 달
            </Button>
          )}
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={sortedItems} rowKey="id" onRowClick={openDetail} />}
      </div>

      <ReservationListModal open={reservationModal.open} scheduleId={reservationModal.scheduleId} scheduleType="session" title={reservationModal.title} onClose={closeReservations} onChanged={handleReservationChanged} />

      <ScheduleDetailModal open={detailModal.open} scheduleId={detailModal.scheduleId} onClose={closeDetail} onSaved={fetchList} />
    </div>
  );
};

export default InstructorScheduleList;
